import React, { forwardRef, useMemo, useState } from 'react'
import { Collapse } from 'reactstrap'
import moment from 'moment-timezone'
import ReactMarkdown from 'react-markdown'
import RepAvatar from '../Material/RepAvatar'
import DatetimeRenderer from '../Material/DatetimeRenderer'
import MessageStatus from './MessageStatus'
import './MessageStatus.css'
import { htmlToPlainText } from './SendMessage/htmlText'
import { splitQuotedHtml } from './quotedHtml'
import EmailMessageBody from './EmailMessageBody'

const COLLAPSED_PREVIEW_LENGTH = 160

const getAttachmentGlyph = (mimeTypeOrName) => {
  const value = mimeTypeOrName?.toLowerCase() || ''
  if (value.includes('pdf') || value.endsWith('.pdf')) return '📄'
  if (value.includes('sheet') || value.endsWith('.xls') || value.endsWith('.xlsx') || value.endsWith('.csv')) return '📊'
  if (value.includes('word') || value.endsWith('.doc') || value.endsWith('.docx')) return '📝'
  if (value.includes('video') || value.endsWith('.mp4') || value.endsWith('.mov')) return '🎥'
  if (value.includes('audio') || value.endsWith('.mp3') || value.endsWith('.wav')) return '🎧'
  return '📎'
}

// One message in the feed: header always visible, body inside a Collapse.
const CommsMessageCard = forwardRef(({
  item,
  defaultOpen = false,
  attachments = [],
  topics = [],
  isEditingTopic = false,
  onEditTopic,
  onTopicChange,
}, ref) => {
  // Read once on mount; switching conversations changes the key, remounting with the default.
  const [isOpen, setIsOpen] = useState(defaultOpen)
  const [showQuoted, setShowQuoted] = useState(false)

  const messageBody = item?.message?.body ?? item.body ?? ''
  const messageBodyHtml = item?.message?.body_html ?? item.body_html ?? ''
  const statusDescription = item?.status_description || ''
  const messageDate = item?.scheduled_for_timestamp || item?.timestamp || null
  const topic = item?.topic_name || null
  const messageSubject = item?.message_subject || ''

  const { visibleHtml, quotedHtml } = useMemo(() => splitQuotedHtml(messageBodyHtml), [messageBodyHtml])

  // Built from visibleHtml so the snippet shows the new reply, not the quoted chain under it.
  const preview = useMemo(() => {
    const text = visibleHtml ? htmlToPlainText(visibleHtml) : messageBody
    const collapsed = text.replace(/\s+/g, ' ').trim()
    return collapsed.length > COLLAPSED_PREVIEW_LENGTH
      ? `${collapsed.slice(0, COLLAPSED_PREVIEW_LENGTH)}…`
      : collapsed
  }, [visibleHtml, messageBody])

  const toggle = () => setIsOpen(open => !open)

  return (
    <div
      ref={ref}
      className={`CommsMessage CommsMessageCard ${item.is_inbound ? "CommsMessageUser" : "CommsMessageCustomer"}${isOpen ? '' : ' is-collapsed'}`}
    >
      <RepAvatar keepClose={true} text={item.is_inbound ? item.driver_name : item.created_by_username} repId={item.created_by_graphene_id} />&nbsp;

      <section className="CommsMessageContent">
        {/* Header-only toggle, so selecting text inside a long email doesn't collapse it. */}
        <header
          className="CommsMessageHeader"
          role="button"
          tabIndex={0}
          aria-expanded={isOpen}
          title={isOpen ? 'Collapse message' : 'Expand message'}
          onClick={toggle}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              toggle()
            }
          }}
        >
          <div className="CommsMessageHeaderMain">
            <div className="CommsMessageSender">{item.sender}</div>
            {!isOpen && preview && (
              <div className="CommsMessagePreview">
                {messageSubject ? `${messageSubject} — ${preview}` : preview}
              </div>
            )}
          </div>

          {/* stopPropagation on click AND key so picking a topic doesn't also toggle the card */}
          <span
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
          >
            {isEditingTopic ? (
              <select
                autoFocus
                defaultValue={item.topic_graphene_id || ''}
                onChange={e => onTopicChange(item.graphene_id, e.target.value || null)}
                onBlur={() => onEditTopic(null)}
                className="CommsMessageTopicSelect"
              >
                <option value="">No topic</option>
                {topics.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            ) : topic ? (
              <span
                onClick={() => onEditTopic(item.graphene_id)}
                title="Click to change topic"
                className="CommsMessageTopic"
              >
                {topic}
              </span>
            ) : (
              <span
                onClick={() => onEditTopic(item.graphene_id)}
                title="Add topic"
                className="CommsMessageTopicAdd"
              >
                + Topic
              </span>
            )}
          </span>

          <div
            className="CommsMessageMeta"
            title={messageDate ? moment(messageDate).format('LLLL') : ''}
          >
            {attachments.length > 0 && !isOpen && (
              <i className="fa fa-paperclip" aria-hidden="true" title={`${attachments.length} attachment(s)`}></i>
            )}
            {messageDate ? <DatetimeRenderer datetime={messageDate} /> : null}
            <MessageStatus
              status={item.status}
              isInbound={item.is_inbound}
              statusDescription={statusDescription}
            />
            <i className={`fa fa-chevron-${isOpen ? 'up' : 'down'} CommsMessageChevron`} aria-hidden="true"></i>
          </div>
        </header>

        {/* Body mounts only when open -- an iframe mounted while hidden measures 0 height. */}
        <Collapse isOpen={isOpen}>
          {item.recipient && <div className="CommsMessageRecipient">To: {item.recipient}</div>}
          {item.cc && <div className="CommsMessageRecipient">Cc: {item.cc}</div>}
          {item.bcc && <div className="CommsMessageRecipient">Bcc: {item.bcc}</div>}
          {item.reply_to && <div className="CommsMessageRecipient">Reply-To: {item.reply_to}</div>}

          <div className="CommsMessageBody">
            {/* Dangerously set html */}
            {messageSubject &&
              <div className="CommsMessageSubject">
                Subject: {messageSubject}
              </div>
            }
            {messageBodyHtml ?
              (isOpen && <EmailMessageBody html={visibleHtml} />)
              :
              messageBody ?
                (<ReactMarkdown>{messageBody}</ReactMarkdown>)
                : null
            }

            {quotedHtml && (
              <>
                <button
                  type="button"
                  className="CommsMessageQuoteToggle"
                  aria-expanded={showQuoted}
                  title={showQuoted ? 'Hide quoted history' : 'Show quoted history'}
                  onClick={() => setShowQuoted(shown => !shown)}
                >
                  •••
                </button>
                {/* Framed only once opened: an iframe in a display:none Collapse measures its height as zero. */}
                <Collapse isOpen={showQuoted}>
                  {showQuoted && <EmailMessageBody html={quotedHtml} title="Quoted email history" />}
                </Collapse>
              </>
            )}
          </div>

          {attachments.length > 0 && (
            <div className="CommsMessageAttachments">
              {attachments.map((resolved) => {
                return (
                  <a
                    key={resolved.attachmentId}
                    href={resolved.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`CommsMessageAttachmentCard${resolved.isImage ? ' is-image' : ''}`}
                  >
                    {resolved.isImage ? (
                      <>
                        <div className="CommsMessageAttachmentImage">
                          <img src={resolved.url} alt={resolved.name} />
                        </div>
                        <div className="CommsMessageAttachmentCaption">
                          <span className="CommsMessageAttachmentName">{resolved.name}</span>
                        </div>
                      </>
                    ) : (
                      <div className="CommsMessageAttachmentMeta">
                        <div className="CommsMessageAttachmentIcon">
                          {getAttachmentGlyph(resolved.extension || resolved.name)}
                        </div>
                        <div className="CommsMessageAttachmentText">
                          <span className="CommsMessageAttachmentName">{resolved.name}</span>
                        </div>
                      </div>
                    )}
                  </a>
                )
              })}
            </div>
          )}
        </Collapse>
      </section>
    </div>
  )
})

export default CommsMessageCard
